import { db, storage, functions } from '../firebase';
import { httpsCallable } from 'firebase/functions';
import { DOUBLE_BAGEL_UID, DOUBLE_BAGEL_NAME } from '../constants';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  onSnapshot,
  increment,
  arrayUnion,
  deleteField,
} from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL, uploadBytes } from 'firebase/storage';
import type { Conversation, Message, MessageType, MessageEditRecord, ReactionKey } from '../types/chat';
import { getUserProfile } from './userProfile';
import { generateThumbnailFromFile } from '../utils/videoThumbnail';
import { chatParticipantName } from '../utils/participantName';
import { byLastMessage, isoOf } from '../utils/firestoreTime';

/**
 * Creates or finds an existing conversation between two users
 * Conversations are unified communication channels that can contain both messages and lessons
 */
export async function createConversation(
  participant1Id: string,
  participant2Id: string
): Promise<string> {
  // Always reuse existing conversation between two users
  const existingConversation = await findConversationBetweenUsers(
    participant1Id,
    participant2Id
  );

  if (existingConversation) {
    return existingConversation.id;
  }

  // Get user profiles for participant details
  const participant1 = await getUserProfile(participant1Id);
  const participant2 = await getUserProfile(participant2Id);

  if (!participant1 || !participant2) {
    throw new Error('User profiles not found');
  }

  // Create conversation document
  const conversationRef = doc(collection(db, 'conversations'));
  const conversationId = conversationRef.id;

  const conversationData: Record<string, unknown> = {
    id: conversationId,
    participants: [participant1Id, participant2Id],
    participantDetails: {
      [participant1Id]: {
        name: chatParticipantName(participant1),
        ...(participant1.avatarUrl && { avatarUrl: participant1.avatarUrl }),
      },
      [participant2Id]: {
        name: chatParticipantName(participant2),
        ...(participant2.avatarUrl && { avatarUrl: participant2.avatarUrl }),
      },
    },
    unreadCount: {
      [participant1Id]: 0,
      [participant2Id]: 0,
    },
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    lastMessageAt: serverTimestamp(), // Initialize so conversation appears in inbox
  };

  await setDoc(conversationRef, conversationData);

  return conversationId;
}

/**
 * Helper function to find existing conversation between two users
 * Returns the first conversation found between the two users (regardless of lesson content)
 */
async function findConversationBetweenUsers(
  user1Id: string,
  user2Id: string
): Promise<Conversation | null> {
  // Query for all conversations containing user1Id
  const q = query(
    collection(db, 'conversations'),
    where('participants', 'array-contains', user1Id)
  );

  const snapshot = await getDocs(q);

  // One thread per pair (functions/src/threads.ts): the oldest thread with
  // exactly the two of them, whatever made it, the same one the server
  // posts to.
  const created = (v: unknown) => typeof v === 'string' ? Date.parse(v) || 0
    : (v as { toMillis?: () => number } | null)?.toMillis?.() ?? 0;
  const pair = snapshot.docs
    .filter((d) => {
      const p = (d.data().participants ?? []) as string[];
      return p.length === 2 && p.includes(user2Id);
    })
    .sort((a, b) => created(a.data().createdAt) - created(b.data().createdAt) || a.id.localeCompare(b.id));
  const doc = pair[0];
  if (!doc) return null;
  const data = doc.data();
  return {
    ...data,
    createdAt: typeof data.createdAt === 'string' ? data.createdAt : data.createdAt?.toDate?.().toISOString() ?? new Date().toISOString(),
    lastMessageAt: isoOf(data.lastMessageAt),
    updatedAt: isoOf(data.updatedAt) || new Date().toISOString(),
  } as Conversation;
}


/**
 * Sends a message in a conversation
 */
export async function sendMessage(
  conversationId: string,
  senderId: string,
  text: string,
  type: MessageType = 'text',
  replyData?: {
    replyToMessageId: string;
    replyToText: string;
    replyToSenderName: string;
    replyToType: MessageType;
  },
  lessonId?: string,
  staffSender?: { id: string; name: string },
  /** progressReports doc ID — set only when writing a `progress_report_delivered`
   *  message. Lands as a top-level field on the message doc; the viewer modal
   *  reads it to hydrate the report. */
  progressReportId?: string,
): Promise<string> {
  // Get sender profile — skipped for staff senders who may not have a user doc
  const senderProfile = staffSender ? null : await getUserProfile(senderId);

  if (!senderProfile && !staffSender) {
    throw new Error('Sender profile not found');
  }

  // Get conversation to find recipient
  const conversation = await getConversation(conversationId);

  if (!conversation) {
    throw new Error('Conversation not found');
  }

  // Create message document
  const messageRef = doc(collection(db, 'conversations', conversationId, 'messages'));
  const messageId = messageRef.id;

  const messageData: Record<string, unknown> = {
    id: messageId,
    conversationId,
    senderId,
    senderName: staffSender?.name ?? chatParticipantName(senderProfile),
    ...(senderProfile?.avatarUrl && { senderAvatarUrl: senderProfile.avatarUrl }),
    type,
    text,
    // Add reply data if present
    ...(replyData && {
      replyToMessageId: replyData.replyToMessageId,
      replyToText: replyData.replyToText,
      replyToSenderName: replyData.replyToSenderName,
      replyToType: replyData.replyToType,
    }),
    // Add lesson reference if present
    ...(lessonId && { lessonId }),
    // Add progress-report reference if present (set on 'progress_report_delivered')
    ...(progressReportId && { progressReportId }),
    // Staff attribution — visible only in the admin support view
    ...(staffSender && { sentByStaffId: staffSender.id, sentByStaffName: staffSender.name }),
    createdAt: serverTimestamp(),
    readBy: [senderId], // Sender has "read" their own message
  };

  await setDoc(messageRef, messageData);

  // Update conversation with last message info
  const recipientId = conversation.participants.find((id) => id !== senderId);

  const conversationRef = doc(db, 'conversations', conversationId);
  await updateDoc(conversationRef, {
    lastMessage: text,
    lastMessageAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    [`unreadCount.${recipientId}`]: increment(1),
  });

  return messageId;
}

/**
 * Sends a pro player video as an attachment in a conversation. The videoUrl
 * is snapshotted at send time so future edits or deletions of the source
 * pro video document do not affect messages already sent.
 */
export async function sendProVideoAttachmentMessage(
  conversationId: string,
  senderId: string,
  proVideo: {
    proVideoId: string;
    proVideoTitle: string;
    proVideoPlayerName?: string;
    proVideoTags?: string[];
    proVideoUrl: string;
    proVideoThumbnailUrl?: string;
    proVideoDurationSeconds?: number;
  }
): Promise<string> {
  const senderProfile = await getUserProfile(senderId);
  if (!senderProfile) throw new Error('Sender profile not found');

  const conversation = await getConversation(conversationId);
  if (!conversation) throw new Error('Conversation not found');

  const messageRef = doc(collection(db, 'conversations', conversationId, 'messages'));
  const messageId = messageRef.id;

  const messageData: Record<string, unknown> = {
    id: messageId,
    conversationId,
    senderId,
    senderName: chatParticipantName(senderProfile),
    ...(senderProfile.avatarUrl && { senderAvatarUrl: senderProfile.avatarUrl }),
    type: 'pro_video_attachment',
    proVideoId: proVideo.proVideoId,
    proVideoTitle: proVideo.proVideoTitle,
    proVideoUrl: proVideo.proVideoUrl,
    ...(proVideo.proVideoPlayerName && { proVideoPlayerName: proVideo.proVideoPlayerName }),
    ...(proVideo.proVideoTags?.length && { proVideoTags: proVideo.proVideoTags }),
    ...(proVideo.proVideoThumbnailUrl && { proVideoThumbnailUrl: proVideo.proVideoThumbnailUrl }),
    ...(proVideo.proVideoDurationSeconds && { proVideoDurationSeconds: proVideo.proVideoDurationSeconds }),
    createdAt: serverTimestamp(),
    readBy: [senderId],
  };

  await setDoc(messageRef, messageData);

  const recipientId = conversation.participants.find((id) => id !== senderId);
  const conversationRef = doc(db, 'conversations', conversationId);
  const displayName = proVideo.proVideoPlayerName
    ? `${proVideo.proVideoPlayerName} · ${proVideo.proVideoTitle}`
    : proVideo.proVideoTitle;
  await updateDoc(conversationRef, {
    lastMessage: `Pro Video: ${displayName}`,
    lastMessageAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    [`unreadCount.${recipientId}`]: increment(1),
  });

  return messageId;
}

/**
 * Sends a drill as an attachment in a conversation. No file upload — only
 * drill metadata and the original videoUrl are stored on the message doc.
 * Both participants can open the video from the card that appears in chat.
 */
export async function sendDrillAttachmentMessage(
  conversationId: string,
  senderId: string,
  drill: {
    drillId: string;
    drillTitle: string;
    drillDescription?: string;
    drillShotType?: string;
    drillVideoUrl: string;
    drillThumbnailUrl?: string;
    drillDurationSeconds?: number;
  }
): Promise<string> {
  const senderProfile = await getUserProfile(senderId);
  if (!senderProfile) throw new Error('Sender profile not found');

  const conversation = await getConversation(conversationId);
  if (!conversation) throw new Error('Conversation not found');

  const messageRef = doc(collection(db, 'conversations', conversationId, 'messages'));
  const messageId = messageRef.id;

  const messageData: Record<string, unknown> = {
    id: messageId,
    conversationId,
    senderId,
    senderName: chatParticipantName(senderProfile),
    ...(senderProfile.avatarUrl && { senderAvatarUrl: senderProfile.avatarUrl }),
    type: 'drill_attachment',
    drillId: drill.drillId,
    drillTitle: drill.drillTitle,
    drillVideoUrl: drill.drillVideoUrl,
    ...(drill.drillDescription && { drillDescription: drill.drillDescription }),
    ...(drill.drillShotType && { drillShotType: drill.drillShotType }),
    ...(drill.drillThumbnailUrl && { drillThumbnailUrl: drill.drillThumbnailUrl }),
    ...(drill.drillDurationSeconds && { drillDurationSeconds: drill.drillDurationSeconds }),
    createdAt: serverTimestamp(),
    readBy: [senderId],
  };

  await setDoc(messageRef, messageData);

  const recipientId = conversation.participants.find((id) => id !== senderId);
  const conversationRef = doc(db, 'conversations', conversationId);
  await updateDoc(conversationRef, {
    lastMessage: `Drill: ${drill.drillTitle}`,
    lastMessageAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    [`unreadCount.${recipientId}`]: increment(1),
  });

  return messageId;
}

/**
 * Uploads a video to storage and returns the URL and storage path
 */
export async function uploadVideo(
  videoFile: Blob,
  uploadPath: string,
  onProgress?: (progress: number) => void
): Promise<{ videoUrl: string; storagePath: string }> {
  const storageRef = ref(storage, uploadPath);
  const uploadTask = uploadBytesResumable(storageRef, videoFile);

  // Track upload progress
  await new Promise<void>((resolve, reject) => {
    uploadTask.on(
      'state_changed',
      (snapshot) => {
        const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        onProgress?.(progress);
      },
      (error) => {
        console.error('Video upload error:', error);
        reject(error);
      },
      () => {
        resolve();
      }
    );
  });

  // Get download URL
  const videoUrl = await getDownloadURL(uploadTask.snapshot.ref);

  return { videoUrl, storagePath: uploadPath };
}

/**
 * Sends a video message in a conversation
 */
export async function sendVideoMessage(
  conversationId: string,
  senderId: string,
  videoFile: Blob,
  text?: string,
  onProgress?: (progress: number) => void,
  replyData?: {
    replyToMessageId: string;
    replyToText: string;
    replyToSenderName: string;
    replyToType: MessageType;
  }
): Promise<string> {
  // Get sender profile
  const senderProfile = await getUserProfile(senderId);

  if (!senderProfile) {
    throw new Error('Sender profile not found');
  }

  // Get conversation to find recipient
  const conversation = await getConversation(conversationId);

  if (!conversation) {
    throw new Error('Conversation not found');
  }

  // Upload video to Firebase Storage
  const timestamp = Date.now();
  const storagePath = `conversations/${conversationId}/videos/${senderId}-${timestamp}.mp4`;
  const storageRef = ref(storage, storagePath);
  const uploadTask = uploadBytesResumable(storageRef, videoFile);

  // Track upload progress
  await new Promise<void>((resolve, reject) => {
    uploadTask.on(
      'state_changed',
      (snapshot) => {
        const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        onProgress?.(progress);
      },
      (error) => {
        console.error('Video upload error:', error);
        reject(error);
      },
      () => {
        resolve();
      }
    );
  });

  // Get download URL
  const videoUrl = await getDownloadURL(uploadTask.snapshot.ref);

  // Generate and upload thumbnail
  let thumbnailUrl: string | undefined;
  try {
    const thumbnailBlob = await generateThumbnailFromFile(videoFile, 1);
    const thumbnailPath = storagePath.replace(/\.mp4$/i, '_thumbnail.jpg');
    const thumbnailRef = ref(storage, thumbnailPath);
    await uploadBytes(thumbnailRef, thumbnailBlob);
    thumbnailUrl = await getDownloadURL(thumbnailRef);
  } catch (error) {
    console.error('Error generating thumbnail (continuing without it):', error);
    // Continue without thumbnail if generation fails
  }

  // Create message document
  const messageRef = doc(collection(db, 'conversations', conversationId, 'messages'));
  const messageId = messageRef.id;

  const messageData: Record<string, unknown> = {
    id: messageId,
    conversationId,
    senderId,
    senderName: chatParticipantName(senderProfile),
    ...(senderProfile.avatarUrl && { senderAvatarUrl: senderProfile.avatarUrl }),
    type: 'video' as MessageType,
    ...(text && { text }),
    videoUrl,
    videoStoragePath: storagePath,
    ...(thumbnailUrl && { thumbnailUrl }),
    // Add reply data if present
    ...(replyData && {
      replyToMessageId: replyData.replyToMessageId,
      replyToText: replyData.replyToText,
      replyToSenderName: replyData.replyToSenderName,
      replyToType: replyData.replyToType,
    }),
    createdAt: serverTimestamp(),
    readBy: [senderId], // Sender has "read" their own message
  };

  await setDoc(messageRef, messageData);

  // Update conversation with last message info
  const recipientId = conversation.participants.find((id) => id !== senderId);

  const conversationRef = doc(db, 'conversations', conversationId);
  await updateDoc(conversationRef, {
    lastMessage: text || '📹 Video',
    lastMessageAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    [`unreadCount.${recipientId}`]: increment(1),
  });

  return messageId;
}

/**
 * Gets a conversation by ID
 */
export async function getConversation(conversationId: string): Promise<Conversation | null> {
  const conversationRef = doc(db, 'conversations', conversationId);
  const conversationSnap = await getDoc(conversationRef);

  if (!conversationSnap.exists()) {
    return null;
  }

  const data = conversationSnap.data();
  return {
    ...data,
    createdAt: typeof data.createdAt === 'string' ? data.createdAt : data.createdAt?.toDate?.().toISOString() ?? new Date().toISOString(),
    lastMessageAt: isoOf(data.lastMessageAt),
    updatedAt: isoOf(data.updatedAt) || new Date().toISOString(),
  } as Conversation;
}

/**
 * Gets all conversations for a user
 */
export async function getAllUserConversations(userId: string): Promise<Conversation[]> {
  const q = query(
    collection(db, 'conversations'),
    where('participants', 'array-contains', userId),
    orderBy('lastMessageAt', 'desc')
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => {
    const data = doc.data();
    return {
      ...data,
      createdAt: typeof data.createdAt === 'string' ? data.createdAt : data.createdAt?.toDate?.().toISOString() ?? new Date().toISOString(),
      lastMessageAt: isoOf(data.lastMessageAt),
      updatedAt: isoOf(data.updatedAt) || new Date().toISOString(),
    } as Conversation;
  }).sort(byLastMessage);
}

/**
 * Subscribes to real-time updates for a conversation's messages
 */
export function subscribeToConversation(
  conversationId: string,
  callback: (messages: Message[]) => void
): () => void {
  const q = query(
    collection(db, 'conversations', conversationId, 'messages'),
    orderBy('createdAt', 'asc')
  );

  const unsubscribe = onSnapshot(q, (snapshot) => {
    const messages = snapshot.docs.map((doc) => {
      const data = doc.data();
      return {
        ...data,
        // The server's lines store no id of their own (store.ts postThreadMessage): the document's.
        id: (data.id as string | undefined) ?? doc.id,
        createdAt: typeof data.createdAt === 'string' ? data.createdAt : data.createdAt?.toDate?.().toISOString() ?? new Date().toISOString(),
        editedAt: data.editedAt?.toDate?.().toISOString(),
        deletedAt: data.deletedAt?.toDate?.().toISOString(),
      } as Message;
    });
    callback(messages);
  });

  return unsubscribe;
}

/**
 * Edits the text of a message and appends a record to its edit history.
 */
export async function editMessage(
  conversationId: string,
  messageId: string,
  newText: string,
  editedBy: string,
  editedByName: string,
  previousText: string,
): Promise<void> {
  const messageRef = doc(db, 'conversations', conversationId, 'messages', messageId);
  const editRecord: MessageEditRecord = {
    previousText,
    editedAt: new Date().toISOString(),
    editedBy,
    editedByName,
  };
  await updateDoc(messageRef, {
    text: newText,
    editedAt: serverTimestamp(),
    editHistory: arrayUnion(editRecord),
  });
}

/**
 * Sets, changes or clears the caller's Tapback on a message.
 *
 * One reaction per person, held as `reactions.<uid>`, so every case here is a
 * single field write against a dotted path. That matters for two reasons: two
 * people reacting at the same instant touch different fields and cannot clobber
 * each other, and the Firestore rule can allow a non-sender to write reactions
 * without also handing them the message text.
 *
 * Passing the reaction the user already has clears it, which is what makes the
 * picker a toggle.
 */
export async function setMessageReaction(
  conversationId: string,
  messageId: string,
  userId: string,
  reaction: ReactionKey | null,
): Promise<void> {
  const messageRef = doc(db, 'conversations', conversationId, 'messages', messageId);
  await updateDoc(messageRef, {
    [`reactions.${userId}`]: reaction ?? deleteField(),
  });
}

/**
 * Soft-deletes a message. The document is retained so conversation ordering
 * is preserved; the bubble renders a tombstone in place of the original content.
 */
export async function deleteMessage(
  conversationId: string,
  messageId: string,
  deletedBy: string,
): Promise<void> {
  const messageRef = doc(db, 'conversations', conversationId, 'messages', messageId);
  await updateDoc(messageRef, {
    isDeleted: true,
    deletedAt: serverTimestamp(),
    deletedBy,
  });
}

/**
 * Subscribes to real-time updates for a user's conversations
 */
export function subscribeToUserConversations(
  userId: string,
  callback: (conversations: Conversation[]) => void
): () => void {
  const q = query(
    collection(db, 'conversations'),
    where('participants', 'array-contains', userId),
    orderBy('lastMessageAt', 'desc')
  );

  const unsubscribe = onSnapshot(q, (snapshot) => {
    const conversations = snapshot.docs.map((doc) => {
      const data = doc.data();
      return {
        ...data,
        createdAt: typeof data.createdAt === 'string' ? data.createdAt : data.createdAt?.toDate?.().toISOString() ?? new Date().toISOString(),
        lastMessageAt: isoOf(data.lastMessageAt),
        updatedAt: isoOf(data.updatedAt) || new Date().toISOString(),
      } as Conversation;
    });

    // Newest first on the client: a server-written ISO time sorts apart from Timestamps in the query (A16).
    conversations.sort(byLastMessage);
    // Pin the Double Bagel support conversation to the top; deduplicate if >1 exists (take most recent)
    const dbConvs = conversations.filter(c => c.participants.includes(DOUBLE_BAGEL_UID));
    const rest = conversations.filter(c => !c.participants.includes(DOUBLE_BAGEL_UID));
    const pinnedDb = dbConvs.length > 0 ? [dbConvs[0]] : [];
    callback([...pinnedDb, ...rest]);
  });

  return unsubscribe;
}

/**
 * Gets or creates the pinned Double Bagel support conversation for a user.
 * Called whenever "Contact Support" is tapped and lazily from the Inbox.
 * Uses the Double Bagel fixture document instead of a real user profile fetch.
 *
 * The welcome message is not written here. It is posted as Double Bagel, which
 * the messages create rule denies to a non-staff client, so the
 * onUserCreatedSupportWelcome Cloud Function writes it when the user doc is
 * created. New conversations use the same `support_<uid>` id as that function,
 * so a create racing it lands on one document rather than two.
 */
export async function getOrCreateSupportConversation(
  userId: string,
  userDisplayName: string,
  userAvatarUrl?: string | null
): Promise<string> {
  const existing = await findConversationBetweenUsers(userId, DOUBLE_BAGEL_UID);
  if (existing) return existing.id;

  const conversationId = `support_${userId}`;
  const conversationRef = doc(db, 'conversations', conversationId);

  // Build the welcome greeting. If displayName is missing, empty, or the legacy
  // 'User' fallback, drop the comma-name entirely rather than rendering the
  // literal word "User" — that fallback can leak into the conversation when the
  // support thread is created before onboarding collects the user's real name.
  const trimmedName = (userDisplayName ?? '').trim();
  const hasRealName = trimmedName.length > 0 && trimmedName !== 'User';
  const greeting = hasRealName
    ? `Welcome to Double Bagel, ${trimmedName}!`
    : 'Welcome to Double Bagel!';

  await setDoc(conversationRef, {
    id: conversationId,
    participants: [userId, DOUBLE_BAGEL_UID],
    participantDetails: {
      [userId]: {
        name: hasRealName ? trimmedName : 'Player',
        ...(userAvatarUrl ? { avatarUrl: userAvatarUrl } : {}),
      },
      [DOUBLE_BAGEL_UID]: {
        name: DOUBLE_BAGEL_NAME,
        avatarUrl: null,
      },
    },
    unreadCount: { [userId]: 0, [DOUBLE_BAGEL_UID]: 0 },
    isPinned: true,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    lastMessageAt: serverTimestamp(),
    lastMessage: greeting,
    lastMessageSenderId: DOUBLE_BAGEL_UID,
  });

  return conversationId;
}

/**
 * Marks all messages in a conversation as read for a specific user
 */
export async function markMessagesAsRead(
  conversationId: string,
  userId: string
): Promise<void> {
  // Get all unread messages for this user
  const messagesRef = collection(db, 'conversations', conversationId, 'messages');
  const q = query(messagesRef, where('readBy', 'not-in', [[userId]]));

  const snapshot = await getDocs(q);

  // Update each message to add userId to readBy array
  const updatePromises = snapshot.docs.map((doc) =>
    updateDoc(doc.ref, {
      readBy: arrayUnion(userId),
    })
  );

  await Promise.all(updatePromises);

  // Reset unread count for this user in the conversation
  const conversationRef = doc(db, 'conversations', conversationId);
  await updateDoc(conversationRef, {
    [`unreadCount.${userId}`]: 0,
  });
}

/**
 * Updates participant details (name, avatar) in all conversations for a user
 * This should be called when a user updates their profile
 */
export async function updateParticipantDetailsInConversations(
  userId: string,
  updates: { name?: string; avatarUrl?: string | null }
): Promise<void> {
  // Get all conversations where the user is a participant
  const q = query(
    collection(db, "conversations"),
    where("participants", "array-contains", userId)
  );

  const snapshot = await getDocs(q);

  // Update each conversation
  const updatePromises = snapshot.docs.map(async (conversationDoc) => {
    const conversationRef = doc(db, "conversations", conversationDoc.id);
    const updateData: Record<string, unknown> = {};

    if (updates.name !== undefined) {
      updateData[`participantDetails.${userId}.name`] = updates.name;
    }

    if (updates.avatarUrl !== undefined) {
      if (updates.avatarUrl) {
        updateData[`participantDetails.${userId}.avatarUrl`] = updates.avatarUrl;
      } else {
        // If avatarUrl is null, we should remove it from the document
        // Firestore does not have a direct way to delete a nested field in updateDoc
        // We need to read the doc, modify it, and write it back
        const conversationData = conversationDoc.data();
        const participantDetails = conversationData.participantDetails || {};
        if (participantDetails[userId]) {
          delete participantDetails[userId].avatarUrl;
          updateData[`participantDetails.${userId}`] = participantDetails[userId];
        }
      }
    }

    if (Object.keys(updateData).length > 0) {
      await updateDoc(conversationRef, updateData);
    }
  });

  await Promise.all(updatePromises);
}

export function subscribeToDoubleBagelConversations(
  callback: (conversations: Conversation[]) => void
): () => void {
  const q = query(
    collection(db, 'conversations'),
    where('participants', 'array-contains', DOUBLE_BAGEL_UID),
    orderBy('lastMessageAt', 'desc')
  );
  return onSnapshot(q, (snapshot) => {
    const conversations = snapshot.docs.map((docSnap) => {
      const data = docSnap.data();
      return {
        ...data,
        id: docSnap.id,
        createdAt: typeof data.createdAt === 'string' ? data.createdAt : data.createdAt?.toDate?.().toISOString() ?? new Date().toISOString(),
        lastMessageAt: data.lastMessageAt?.toDate?.().toISOString(),
        updatedAt: data.updatedAt?.toDate?.().toISOString() || new Date().toISOString(),
      } as Conversation;
    });
    callback(conversations);
  });
}

export async function broadcastMessage(text: string): Promise<void> {
  const fn = httpsCallable(functions, 'adminBroadcastMessage');
  await fn({ text });
}

/**
 * Set typing state for a user in a conversation.
 * Writes a server timestamp when typing, or deletes the key when stopped.
 */
export async function setTypingState(
  conversationId: string,
  userId: string,
  isTyping: boolean
): Promise<void> {
  const conversationRef = doc(db, 'conversations', conversationId);
  await updateDoc(conversationRef, {
    [`typing.${userId}`]: isTyping ? serverTimestamp() : null,
  });
}


/** Support's lines in a thread, for the support thread's badge (utils/supportUnread). */
export function subscribeSupportLines(
  conversationId: string,
  onChange: (lines: Array<{ senderId?: string; readBy?: string[] }>) => void,
): () => void {
  return onSnapshot(
    query(collection(db, 'conversations', conversationId, 'messages'), where('senderId', '==', DOUBLE_BAGEL_UID)),
    (snap) => onChange(snap.docs.map((d) => d.data() as { senderId?: string; readBy?: string[] })),
    () => onChange([]),
  );
}
